import * as React from 'react';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import {
  setFilterMode,
  updateCustomDateRange,
  updateFilter,
} from '../../redux/features/filter/filterSlice';

const CUSTOM_RANGE_VALUE = 'CUSTOM_RANGE';

function Search() {
  const filterState = useSelector((state) => state.filter);
  const dispatch = useDispatch();
  const [draftStartDate, setDraftStartDate] = React.useState(filterState.customStartDate || '');
  const [draftEndDate, setDraftEndDate] = React.useState(filterState.customEndDate || '');
  const today = React.useMemo(() => new Date().toISOString().slice(0, 10), []);

  const selectedValue = filterState.mode === 'custom'
    ? CUSTOM_RANGE_VALUE
    : filterState.value;

  React.useEffect(() => {
    setDraftStartDate(filterState.customStartDate || '');
    setDraftEndDate(filterState.customEndDate || '');
  }, [filterState.customEndDate, filterState.customStartDate]);

  const handleChange = (event) => {
    const selectedFilter = event.target.value;

    if (selectedFilter === CUSTOM_RANGE_VALUE) {
      dispatch(setFilterMode('custom'));
      return;
    }

    dispatch(updateFilter(selectedFilter));
  }

  const handleDateChange = (fieldName, fieldValue) => {
    const nextStartDate = fieldName === 'startDate' ? fieldValue : draftStartDate;
    const nextEndDate = fieldName === 'endDate' ? fieldValue : draftEndDate;

    if (fieldName === 'startDate')
      setDraftStartDate(fieldValue);
    else
      setDraftEndDate(fieldValue);

    if (!nextStartDate || !nextEndDate)
      return;

    if (nextStartDate > today || nextEndDate > today)
      return;

    if (nextStartDate > nextEndDate)
      return;

    dispatch(updateCustomDateRange({
      startDate: nextStartDate,
      endDate: nextEndDate,
    }));
    dispatch(setFilterMode('custom'));
  };

  const options = [
    { value: 'ONE_DAY', label: '1 dia' },
    { value: 'SEVEN_DAYS', label: '7 dias' },
    { value: 'ONE_MONTH', label: '30 dias' },
    { value: 'THREE_MONTHS', label: '3 meses' },
    { value: 'SIX_MONTHS', label: '6 meses' },
    { value: 'ONE_YEAR', label: '1 ano' },
    { value: 'FIVE_YEARS', label: '5 anos' },
    { value: CUSTOM_RANGE_VALUE, label: 'Personalizado' },
  ]

  const isInvalidRange = Boolean(draftStartDate && draftEndDate && draftStartDate > draftEndDate);
  const isInvalidFutureDate = Boolean(
    (draftStartDate && draftStartDate > today)
    || (draftEndDate && draftEndDate > today),
  );

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
      <FormControl sx={{ width: { xs: '100%', md: '25ch' } }} variant="outlined">
        <InputLabel id="filter-selection">Período</InputLabel>
        <Select
          labelId="filter-selection"
          id="filter-value"
          value={selectedValue}
          label="Período"
          onChange={handleChange}
          size='small'
          sx={{ flexGrow: 1 }}
        >
          {options.map((option, index) => (
            <MenuItem value={option.value} key={index}>{option.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {filterState.mode === 'custom' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <TextField
            size="small"
            type="date"
            label="Início"
            value={draftStartDate}
            onChange={(event) => handleDateChange('startDate', event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today }}
            error={isInvalidFutureDate}
            helperText={isInvalidFutureDate ? 'A data não pode ser maior que hoje' : ''}
            sx={{ minWidth: { xs: '100%', sm: 170 } }}
          />
          <TextField
            size="small"
            type="date"
            label="Fim"
            value={draftEndDate}
            onChange={(event) => handleDateChange('endDate', event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today }}
            error={isInvalidRange || isInvalidFutureDate}
            helperText={
              isInvalidFutureDate
                ? 'A data não pode ser maior que hoje'
                : isInvalidRange
                  ? 'Fim deve ser maior ou igual ao início'
                  : ''
            }
            sx={{ minWidth: { xs: '100%', sm: 170 } }}
          />
        </Stack>
      )}
    </Stack>
  );
}

export default Search;
